/**
 * useAdminTokens hook
 *
 * TanStack Query hook for fetching admin token list from GET /v1/admin/tokens.
 * Supports search, verification filter, and cursor pagination.
 *
 * Story 7.3 — Task 2.3
 */

import type { Token } from "@repo/shared-types";
import { useQuery } from "@tanstack/react-query";

import { type AdminTokenListParams, adminApi } from "../../lib/api";
import { useWallet } from "../use-wallet";
import { adminKeys } from "./query-keys";

export type AdminTokenFilters = {
	search?: string;
	verification?: string;
	cursor?: string;
};

function filtersToKey(filters: AdminTokenFilters): string {
	return JSON.stringify(filters);
}

export type AdminTokenListData = {
	tokens: Token[];
	hasMore: boolean;
};

export function useAdminTokens(filters: AdminTokenFilters) {
	const { isConnected, getAccessToken } = useWallet();

	return useQuery<AdminTokenListData, Error>({
		queryKey: adminKeys.tokenList(filtersToKey(filters)),
		queryFn: async () => {
			const token = await getAccessToken();
			if (!token) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error("No auth token");
			}
			const params: AdminTokenListParams = { limit: 50 };
			if (filters.search) params.search = filters.search;
			if (filters.verification) params.verification = filters.verification;
			if (filters.cursor) params.cursor = filters.cursor;
			const response = await adminApi.getTokens(params, token);
			return response.data;
		},
		enabled: isConnected,
		staleTime: 30_000,
		placeholderData: (previousData) => previousData,
	});
}
